/**
 * Tiny inline-SVG sparkline for KPI tiles.
 *
 * Renders a smooth area + stroke. No axes, no labels, no tooltips —
 * decorative summary of a 30-point series. For interactive charts use
 * Recharts. Width is fluid (parent-bound); height is fixed via prop.
 */

import { useId } from "react";
import { cn } from "@/lib/utils";

interface SparklineProps {
  /** Numeric series — minimum 2 points. */
  points: number[];
  /** Render width in px. Default 80. */
  width?: number;
  /** Render height in px. Default 28. */
  height?: number;
  /** Stroke + fill color. Default uses --color-primary so it inherits palette. */
  color?: string;
  /** Render only line (no fill) — passes the prototype's "line" chart-style toggle. */
  noFill?: boolean;
  className?: string;
}

export function Sparkline({
  points,
  width = 80,
  height = 28,
  color = "var(--color-primary)",
  noFill = false,
  className,
}: SparklineProps) {
  // useId before the early-return so hook order stays stable.
  const reactId = useId();
  const gradientId = `sparkline-gradient-${reactId.replace(/:/g, "")}`;

  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  // Pad y so values at min/max don't sit on the SVG edge and clip the stroke.
  const yPad = 2;
  const usableHeight = height - 2 * yPad;
  const stepX = width / (points.length - 1);
  const yFor = (v: number) => yPad + usableHeight - ((v - min) / range) * usableHeight;

  // Build path: line for stroke, optional closed area for fill.
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(2)},${yFor(p).toFixed(2)}`)
    .join(" ");
  const areaPath = `${linePath} L${width.toFixed(2)},${height} L0,${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-hidden
      className={cn("overflow-visible", className)}
    >
      {!noFill && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.3" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} />
        </>
      )}
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
